import React from 'react';
import PropTypes from 'prop-types';
import { useTooltip } from '@reach/tooltip';
import Portal from '@reach/portal';

import { Popup, Tip, tipWidth, tipHeight } from './index.styles';

const centered = (triggerRect, tooltipRect) => {
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = window.innerWidth - tooltipRect.width - 2;

  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.bottom + tipHeight + window.scrollY,
  };
};

export default function Tooltip({ ariaLabel, children, className, color, faded = false, label }) {
  const [trigger, tooltip] = useTooltip();

  const { isVisible, triggerRect } = tooltip;

  return (
    <>
      {React.cloneElement(children, trigger)}
      {isVisible && (
        <Portal>
          <Tip
            color={color}
            faded={faded}
            style={
              triggerRect && {
                left: triggerRect.left + triggerRect.width / 2 - tipWidth / 2,
                top: triggerRect.bottom + window.scrollY,
              }
            }
          />
        </Portal>
      )}
      <Popup
        {...tooltip}
        aria-label={ariaLabel}
        className={className}
        color={color}
        faded={faded}
        label={label}
        position={centered}
      />
    </>
  );
}

Tooltip.propTypes = {
  /**
   * ARIA label.
   */
  ariaLabel: PropTypes.string,
  /**
   * Component to wrap the tooltip around.
   */
  children: PropTypes.node,
  /**
   * Tooltip class name.
   */
  className: PropTypes.string,
  /**
   * Tooltip color.
   */
  color: PropTypes.oneOf([
    'black',
    // Deprecated, but kept for backwards compatibility
    'red',
    'blue',
    'orange',
    'teal',
    'pink',
  ]),
  /**
   * Whether tooltip background should be faded.
   */
  faded: PropTypes.bool,
  /**
   * Tooltip content.
   */
  label: PropTypes.node,
};
